import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { extractLocaleFromPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { buildConditionPageSchema } from '../../utils/schemaMarkup';
import CTABanner from '../../components/CTABanner/CTABanner';
import taxonomy from '../../data/taxonomy.json';
import './BodyPartPage.css';

// Lookup table for body part injury pages by slug
const bodyPartData = {
  'back-injury': { title: 'Back Injury', desc: 'Comprehensive back injury care from our spine specialists. We treat herniated discs, fractures, muscle strains, and more.', bodyPartSlug: 'back-injury-treatment' },
  'knee-injury': { title: 'Knee Injury', desc: 'Advanced knee injury treatment including ACL repairs, meniscus surgery, and fracture care.', bodyPartSlug: 'knee-injury-treatment' },
  'neck-injury': { title: 'Neck Injury', desc: 'Specialized neck injury treatment for whiplash, herniated discs, and nerve damage.', bodyPartSlug: 'neck-injury-treatment' },
  'shoulder-injury': { title: 'Shoulder Injury', desc: 'Shoulder injury treatment including rotator cuff repair, dislocation care, and shoulder surgery.', bodyPartSlug: 'shoulder-injury-treatment' },
  'hip-injury': { title: 'Hip Injury', desc: 'Hip injury treatment from fractures to joint replacement and physical therapy.', bodyPartSlug: 'hip-injury-treatment' },
  'wrist-injury': { title: 'Wrist Injury', desc: 'Wrist injury treatment including carpal tunnel, fracture repair, and ligament tears.', bodyPartSlug: 'wrist-injury-treatment' },
  'ankle-injury': { title: 'Ankle Injury', desc: 'Expert ankle injury treatment including sprains, fractures, and tendon tears.', bodyPartSlug: 'ankle-injury-treatment' },
  'head-injury': { title: 'Head Injury', desc: 'Traumatic brain injury evaluation, concussion treatment, and neurological care.', bodyPartSlug: 'head-injury-treatment' },
  'elbow-injury': { title: 'Elbow Injury', desc: 'Elbow injury treatment for fractures, dislocations, and nerve compression.', bodyPartSlug: 'elbow-injury-treatment' },
  'foot-injury': { title: 'Foot Injury', desc: 'Foot injury care including stress fractures, plantar fasciitis, and tendon injuries.', bodyPartSlug: 'foot-injury-treatment' },
  'hand-injury': { title: 'Hand Injury', desc: 'Hand injury treatment including tendon repair, fracture care, and nerve decompression.', bodyPartSlug: 'hand-injury-treatment' },
  'spine-injury': { title: 'Spine Injury', desc: 'Advanced spine injury care with minimally invasive surgical and non-surgical options.', bodyPartSlug: 'spine-injury-treatment' },
};

export default function BodyPartPage() {
  const { bodyPartSlug } = useParams();
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand || state.client;

  const bodyPart = bodyPartData[bodyPartSlug];
  const taxonomyEntry = taxonomy.bodyPartInjuryPages?.find(
    (b) => b.slug === (bodyPart?.bodyPartSlug || bodyPartSlug)
  );

  if (!bodyPart) {
    return (
      <div className="container section-padding">
        <h1>Page Not Found</h1>
        <p>The injury page you're looking for doesn't exist.</p>
      </div>
    );
  }

  const pageTitle = `${bodyPart.title} Treatment | ${brand?.name || 'Georgia Spine & Orthopaedics'}`;
  const metaDescription = `${bodyPart.desc} Expert orthopedic care at ${brand?.name}. Free consultation — call ${brand?.phone} today.`;
  const canonicalUrl = `https://${brand?.domain || 'gaspineortho.com'}/${locale}/${bodyPartSlug}`;
  const baseDomain = brand?.domain || 'gaspineortho.com';

  // Get related services from taxonomy entry
  const allServices = taxonomy.dimensions?.find((d) => d.id === 'service')?.values || [];
  const relatedServices = (taxonomyEntry?.relatedServices || [])
    .map((slug) => allServices.find((s) => s.slug === slug))
    .filter(Boolean);

  // Get related conditions
  const relatedConditions = (taxonomyEntry?.relatedConditions || [])
    .map((slug) => taxonomy.conditionPages?.find((c) => c.slug === slug))
    .filter(Boolean);

  const conditionSchema = buildConditionPageSchema({
    condition: { display: bodyPart.title, description: bodyPart.desc },
    brand,
    relatedServices,
  });

  return (
    <div className="body-part-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {locale !== 'en' && <html lang={locale} />}
      </Helmet>

      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/${bodyPartSlug}`} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(conditionSchema) }}
      />

      {/* Hero */}
      <section className="glass-dark body-part-hero" style={{ padding: '80px 0', textAlign: 'center', borderRadius: 0 }}>
        <div className="container">
          <div
            className="glass-badge"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', marginBottom: 16, display: 'inline-block', padding: '4px 16px', borderRadius: 20 }}
          >
            Injury Care
          </div>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
            {bodyPart.title} Treatment
          </h1>
          <p style={{ opacity: 0.9, maxWidth: 620, margin: '0 auto 28px', color: '#fff', fontSize: '1.15rem', lineHeight: 1.7 }}>
            {bodyPart.desc}
          </p>
          <a href="/request-appt" className="glass-btn glass-btn-accent" style={{ marginRight: 12 }}>
            Schedule Consultation
          </a>
          <a href={`tel:${brand?.phone?.replace(/\D/g, '')}`} className="glass-btn" style={{ border: '2px solid rgba(255,255,255,0.6)', color: '#fff' }}>
            Call {brand?.phone}
          </a>
        </div>
      </section>

      <div className="container" style={{ maxWidth: 960, margin: '0 auto', padding: '40px 20px 60px' }}>

        {/* About Treatment */}
        <div className="glass-panel" style={{ padding: 32, marginBottom: 24, borderRadius: 12 }}>
          <h2>About {bodyPart.title} Treatment</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, marginTop: 12 }}>
            At {brand?.name}, our team of orthopedic specialists and spine surgeons provides comprehensive
            treatment for {bodyPart.title.toLowerCase()} injuries. We use the latest diagnostic technology —
            including on-site MRI and X-ray — and minimally invasive techniques to ensure optimal outcomes.
          </p>
          <ul style={{ marginTop: 16, color: 'var(--color-text-secondary)', lineHeight: 2 }}>
            <li>✓ Board-certified orthopedic specialists</li>
            <li>✓ Minimally invasive surgical options</li>
            <li>✓ On-site MRI, X-ray, and diagnostic imaging</li>
            <li>✓ Workers' comp, auto insurance &amp; lien cases accepted</li>
            <li>✓ Multilingual staff — se habla español</li>
          </ul>
        </div>

        {/* Related Services */}
        {relatedServices.length > 0 && (
          <div className="glass-panel" style={{ padding: 32, marginBottom: 24, borderRadius: 12 }}>
            <h2 style={{ marginBottom: 16 }}>Related Services</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {relatedServices.map((svc) => (
                <a
                  key={svc.slug}
                  href={`/${svc.slug}`}
                  className="glass-panel"
                  style={{ padding: '14px 18px', textDecoration: 'none', color: 'var(--color-text)', fontSize: '0.95rem', fontWeight: 600, borderRadius: 10, display: 'block' }}
                >
                  {svc.display}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Related Conditions */}
        {relatedConditions.length > 0 && (
          <div className="glass-panel" style={{ padding: 32, marginBottom: 24, borderRadius: 12 }}>
            <h2 style={{ marginBottom: 16 }}>Related Conditions</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {relatedConditions.map((cond) => (
                <a
                  key={cond.slug}
                  href={`/conditions/${cond.slug}`}
                  style={{ padding: '8px 18px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 20, textDecoration: 'none', color: 'var(--color-primary)', fontSize: '0.9rem', fontWeight: 600 }}
                >
                  {cond.display}
                </a>
              ))}
            </div>
          </div>
        )}

      </div>

      <CTABanner client={brand} />
    </div>
  );
}
